import {
  AppearanceBadAction,
  AppearanceOkAction,
  ContentBadAction,
  ContentOkAction,
  DecisionFlow,
  FlowBranch,
} from '../../types/returnInspection';

/**
 * 库存单元级"按流程初检"的检查流程：骨架固定（外观 → 内件，每个环节只有"一致/不一致"），
 * 每个结果之后做什么由客户在下面限定的选项里选。选项和后端 decision_flow.py 保持一致。
 */

export const APPEARANCE_OK_OPTIONS: Array<{ value: AppearanceOkAction; label: string }> = [
  { value: 'continue', label: '继续检查内件' },
  { value: 'resell', label: '作为正常产品入库（不再检查内件）' },
  { value: 'aiu', label: '按异常库存（AIU）入库' },
];

/** 吸塑/塑封包装：完好的包装无法拆开，"外观一致"之后不能继续检查内件 */
export const appearanceOkOptions = (sealed: boolean) =>
  sealed
    ? APPEARANCE_OK_OPTIONS.filter((option) => option.value !== 'continue')
    : APPEARANCE_OK_OPTIONS;

/** 吸塑/塑封包装的默认流程：包装完好 → 作为正常产品入库；不完好 → 入 AIU */
export const SEALED_DECISION_FLOW: DecisionFlow = {
  appearance_ok: { action: 'resell' },
  appearance_bad: { action: 'aiu' },
};

export const APPEARANCE_BAD_OPTIONS: Array<{ value: AppearanceBadAction; label: string }> = [
  { value: 'aiu', label: '按异常库存（AIU）入库' },
  { value: 'destroy', label: '直接销毁' },
  { value: 'continue', label: '继续检查内件' },
];

export const CONTENT_OK_OPTIONS: Array<{ value: ContentOkAction; label: string }> = [
  { value: 'resell', label: '作为正常产品入库' },
  { value: 'aiu', label: '按异常库存（AIU）入库' },
];

export const CONTENT_BAD_OPTIONS: Array<{ value: ContentBadAction; label: string }> = [
  { value: 'aiu', label: '按异常库存（AIU）入库' },
  { value: 'destroy', label: '直接销毁' },
];

/** 新建规则的默认流程：外观一致 → 继续检查内件；外观不一致 → 入 AIU；内件符合 → 上架，不符 → 入 AIU */
export const DEFAULT_DECISION_FLOW: DecisionFlow = {
  appearance_ok: { action: 'continue', content_ok: 'resell', content_bad: 'aiu' },
  appearance_bad: { action: 'aiu' },
};

const DEFAULT_CONTENT_OK: ContentOkAction = 'resell';
const DEFAULT_CONTENT_BAD: ContentBadAction = 'aiu';

export const branchContinues = (node: FlowBranch | undefined): boolean =>
  node?.action === 'continue';

/** 有没有哪条路径会走到内件检查；没有的话就不需要内件基准资料 */
export const contentStageReachable = (flow: DecisionFlow): boolean =>
  branchContinues(flow.appearance_ok) || branchContinues(flow.appearance_bad);

/** 改某个分支"外观检查之后"的处置：切到"继续检查内件"时补上默认的内件处置，切走时去掉用不上的内件处置 */
export const withBranchAction = (
  flow: DecisionFlow,
  branch: 'appearance_ok' | 'appearance_bad',
  action: string,
): DecisionFlow => {
  const previous = flow[branch];
  const node: FlowBranch =
    action === 'continue'
      ? {
          action: 'continue',
          content_ok: previous.content_ok ?? DEFAULT_CONTENT_OK,
          content_bad: previous.content_bad ?? DEFAULT_CONTENT_BAD,
        }
      : { action: action as FlowBranch['action'] };
  return { ...flow, [branch]: node };
};

const OUTCOME_TEXT: Record<string, string> = {
  resell: '作为正常产品入库',
  aiu: '按异常库存（AIU）入库',
  destroy: '直接销毁',
};

/** 把流程翻译成几行白话，实时显示在抽屉里，客户一眼能确认自己配的是什么 */
export const describeFlow = (flow: DecisionFlow, sealed = false): string[] => {
  const lines: string[] = [];
  if (sealed) lines.push('外包装为吸塑/塑封：按包装是否完好判定外观（外观一致 = 包装完好）');
  const describeBranch = (branch: 'appearance_ok' | 'appearance_bad', title: string) => {
    const node = flow[branch];
    if (!node) return;
    if (node.action === 'continue') {
      lines.push(`${title} → 继续检查内件`);
      lines.push(`　　内件符合 → ${OUTCOME_TEXT[node.content_ok ?? DEFAULT_CONTENT_OK]}`);
      lines.push(`　　内件不符 → ${OUTCOME_TEXT[node.content_bad ?? DEFAULT_CONTENT_BAD]}`);
    } else {
      lines.push(
        `${title} → ${OUTCOME_TEXT[node.action]}${node.action === 'resell' ? '（不再检查内件）' : ''}`,
      );
    }
  };
  describeBranch('appearance_ok', '外观一致');
  describeBranch('appearance_bad', '外观不一致');
  return lines;
};
